import React, { useState, useEffect, useRef } from 'react';
import ModalLayer from './ModalLayer';
import { supabase } from '../supabaseClient';
import { hasAccessToken } from '../utils/authSession';
import { reassignTasks } from '../utils/taskAssignment';
import { devLog } from '../utils/devLog';
import HomeownerImport from './HomeownerImport';
import { useTheme } from '../ThemeContext';
import { secondaryButtonStyle } from '../shared/secondaryButton';
import { isNativeApp, isWebApp, NATIVE_PLAN_NOTICE } from '../utils/platform';
import { agentFetch, inviteColumnLabel, inviteErrorMessage } from '../utils/agentApi';
import { openExternalUrl } from '../utils/openExternal';
import { canonicalPropertyType, homeownerLimit, normalizeFeatureList, propertyTypeChoices, toggleFeature } from '../shared/homeownerIntake';
import { homeProfileColumns, homeProfileSignature, readHomeProfile } from '../shared/homeProfile';
import { featuresFromFacts, homeFactsPatch, homeFactsSignature } from '../shared/homeFacts';
import HomeProfileQuestions from './HomeProfileQuestions';
import HomeFactsFields from './HomeFactsFields';
import { PROPERTY_PHOTO_ACCEPT, propertyPhotoError, uploadAgentPropertyPhoto } from '../utils/propertyPhotoUpload';
import { homeownerPoolRow, homeownerUsageLabel, homeownerUsageLevel, homeownerUsageWarning, isActiveHomeowner, limitReachedMessage, restoredHomeownerStatus } from '../shared/planLimits';
import { activeClientCount } from '../shared/agentActivity';
import HomeownerGraceBanner from './HomeownerGraceBanner';
import ConfirmDialog from './ConfirmDialog';
import { CountyField } from './ServiceAreaFields';
import { showToast } from './Toast';
import {
  buildSystemData,
  featuresAfterInventorySave,
  readBrand,
  readInstallYear,
  systemsMapFromRows,
} from '../shared/inventoryItem';
import { clientCardSubtitle, mergeClientSystems } from '../shared/clientDetail';
import InventoryItemForm from './InventoryItemForm';
import { inviteMessage } from '../shared/inviteTemplate';
import { inviteMailto, invitePopupLine, resolveInviteSend } from '../shared/inviteDelivery';
import { siteBaseFromLocation } from '../shared/siteUrl';
import ClientDetailPanel from './ClientDetailPanel';
import { requestHomeHistoryReport, homeReportEntitlementMap } from './HomeHistoryReportAgent';
import { StoredPropertyPhoto } from './PropertyPhoto';
import { showInviteSendAction } from '../shared/invitePolicy';
import { teamClientOrFilter } from '../shared/teamClients';
import { fieldValue, projectRows } from '../shared/listRefresh';

function factColumns(formData, homePatch) {
  const facts = homeFactsPatch(Object.assign({}, formData, homePatch || {}));
  return Object.assign({}, homePatch || {}, facts, {
    features: featuresFromFacts(formData.features, Object.assign({}, formData, facts)),
  });
}

const emptyHomeownerForm = (agentId) => ({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip_code: '',
  property_county_fips: '',
  property_county_source: '',
  notes: '',
  property_type: 'single_family',
  construction_type: '',
  exterior_finish: '',
  foundation_type: '',
  roof_material: '',
  features: [],
  property_photo_url: '',
  is_team_client: false,
  assigned_agent_id: agentId || null,
  hvac_count: 1,
  hvac_install_years: [null],
  hvac_types: ['Central AC'],
  roof_type: '',
  roof_install_year: null,
  water_heater_type: '',
  water_heater_install_year: null,
  water_source: [],
  irrigation_source: [],
  well_pump_install_year: null,
  sewer_type: [],
  septic_install_year: null,
  pool_pump_install_year: null,
  pool_heater_install_year: null,
  year_built: null,
  square_feet: null,
  filter_type: '',
  hvac_filter_sizes: [],
  roof_year: null,
  warnings: []
});

const propertyTypes = propertyTypeChoices();

function homeProfileInput(formData) {
  return {
    construction_type: formData.construction_type,
    exterior_finish: formData.exterior_finish,
    foundation_type: formData.foundation_type,
    roof_material: formData.roof_material,
    roof_year: formData.roof_year,
    hvac_count: formData.hvac_count,
  };
}

function parsedHomeProfile(formData) {
  const parsed = homeProfileColumns(homeProfileInput(formData));
  return {
    ok: Object.keys(parsed.errors).length === 0,
    errors: parsed.errors,
    patch: parsed.patch,
  };
}

function missedEntry(map, clientId) {
  const entry = map[clientId];
  if (!entry || !entry.count) return null;
  const titles = (entry.titles || []).filter(Boolean);
  const shown = titles.slice(0, 8);
  const extra = titles.length - shown.length;
  const tip = shown.length
    ? shown.join(', ') + (extra > 0 ? `, and ${extra} more` : '')
    : `${entry.count} missed in the last 12 months`;
  return { text: `Missed: ${entry.count}`, tip };
}

function stateCode(value) {
  return String(value || '').trim().toUpperCase();
}

function validState(value) {
  return /^[A-Z]{2}$/.test(stateCode(value));
}

const ClientManagement = ({ profile, onStatsUpdate, onOpenClient, onOpenIntegrations, onNavigate }) => {
  const { colors, isDark } = useTheme();
  const [clients, setClients] = useState([]);
  const [clientsReady, setClientsReady] = useState(false);
  const [poolUsage, setPoolUsage] = useState(null);
  const [poolChecked, setPoolChecked] = useState(false);
  const clientsRequest = useRef(0);
  const savedClientPatches = useRef(new Map());
  const removedClientIds = useRef(new Set());
  const [clientSystems, setClientSystems] = useState({}); // Store systems data by client ID
  const [showModal, setShowModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [batchFilter, setBatchFilter] = useState(null);
  const [inviteLink, setInviteLink] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [missedByClient, setMissedByClient] = useState({});
  const [missedSort, setMissedSort] = useState(false);
  const [editingClient, setEditingClient] = useState(null); // Track which client is being edited
  const [uploading, setUploading] = useState(false); // Track file upload progress
  const [invitePhoto, setInvitePhoto] = useState(null);
  const [invitePhotoName, setInvitePhotoName] = useState('');
  const [formData, setFormData] = useState(() => emptyHomeownerForm(profile?.id));
  const [sendInvite, setSendInvite] = useState(false);
  const [savingHomeowner, setSavingHomeowner] = useState(false);
  const [rebuildingTasks, setRebuildingTasks] = useState(false);
  const [stateError, setStateError] = useState('');
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteSending, setInviteSending] = useState(false);
  const inviteSendingRef = useRef(false);
  const [duplicateClient, setDuplicateClient] = useState(null);
  const [detailClient, setDetailClient] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [inventoryEditor, setInventoryEditor] = useState(null);
  const [savingInventory, setSavingInventory] = useState(false);
  const [archiveClient, setArchiveClient] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [cardMenuId, setCardMenuId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const run = () => {
      if (!cancelled) loadClients();
    };
    run();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!hasAccessToken(session)) return;
      if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        setTimeout(run, 0);
      }
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, [profile?.id, profile?.team_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!profile?.id || typeof supabase.rpc !== 'function') {
      setPoolUsage(null);
      setPoolChecked(true);
      return undefined;
    }
    let cancelled = false;
    const pending = supabase.rpc('homeowner_pool_usage');
    if (!pending || typeof pending.then !== 'function') {
      setPoolChecked(true);
      return undefined;
    }
    pending.then((result) => {
      if (cancelled) return;
      if (result && !result.error) setPoolUsage(homeownerPoolRow(result.data));
      setPoolChecked(true);
    }).catch(() => {
      if (cancelled) return;
      setPoolChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [profile?.id, profile?.team_id, clients.length]);

  useEffect(() => {
    if (!showModal || editingClient) return undefined;
    setSendInvite(false);
    return undefined;
  }, [showModal, editingClient]);


  const fetchClientRows = async () => {
    if (profile.team_id) {
      const { data: members, error: memberError } = await supabase.rpc('team_member_profiles');
      if (memberError) throw memberError;
      const memberIds = (members || []).map((row) => row.id);
      if (profile.id && !memberIds.includes(profile.id)) memberIds.push(profile.id);
      const { data: clientData, error } = await supabase
        .from('clients')
        .select('*, assigned_agent:assigned_agent_id(first_name, last_name)')
        .or(teamClientOrFilter(profile.id, memberIds))
        .order('date_added', { ascending: false });
      if (error) throw error;
      return clientData || [];
    }
    const { data: clientData, error } = await supabase
      .from('clients')
      .select('*')
      .eq('agent_id', profile.id)
      .order('date_added', { ascending: false });
    if (error) throw error;
    return clientData || [];
  };

  const showClients = (rows) => {
    setClients(projectRows(rows, savedClientPatches.current, removedClientIds.current));
  };

  const loadClients = async () => {
    if (!profile?.id) return;
    const { data: { session } } = await supabase.auth.getSession();
    if (!hasAccessToken(session)) return;

    const requestId = ++clientsRequest.current;
    try {
      let rows = await fetchClientRows();
      if (requestId !== clientsRequest.current) return;
      // A request that left before the access token was attached comes back
      // empty under RLS. Confirm the session and try once more before showing 0.
      if (rows.length === 0) {
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (requestId !== clientsRequest.current) return;
        const retry = await supabase.auth.getSession();
        if (!hasAccessToken(retry.data.session)) return;
        rows = await fetchClientRows();
        if (requestId !== clientsRequest.current) return;
      }
      const plans = await homeReportEntitlementMap();
      if (requestId !== clientsRequest.current) return;
      rows = rows.map((row) => (
        Object.prototype.hasOwnProperty.call(plans, row.id)
          ? Object.assign({}, row, { entitled: plans[row.id] })
          : row
      ));
      showClients(rows);
      setClientsReady(true);
      if (rows.length > 0) {
        const ids = rows.map(c => c.id);
        loadClientSystems(ids);
        loadMissedCounts(ids);
      } else {
        setMissedByClient({});
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadMissedCounts = async (clientIds) => {
    try {
      const { data, error } = await supabase
        .from('v_client_missed_counts')
        .select('client_id, missed_12m, missed_titles')
        .in('client_id', clientIds);
      if (error) {
        console.error('Missed counts unavailable', error);
        return;
      }
      const next = {};
      (data || []).forEach((row) => {
        next[row.client_id] = {
          count: Number(row.missed_12m) || 0,
          titles: Array.isArray(row.missed_titles) ? row.missed_titles : [],
        };
      });
      setMissedByClient(next);
    } catch (error) {
      console.error('Missed counts unavailable', error);
    }
  };

  const loadClientSystems = async (clientIds) => {
    try {
      const { data: systemsData, error } = await supabase
        .from('client_systems')
        .select('*')
        .in('client_id', clientIds);

      if (error) throw error;

      // Organize systems by client_id
      const systemsByClient = {};
      (systemsData || []).forEach(system => {
        if (!systemsByClient[system.client_id]) {
          systemsByClient[system.client_id] = [];
        }
        systemsByClient[system.client_id].push(system);
      });

      setClientSystems(systemsByClient);
    } catch (error) {
      console.error('Error loading client systems:', error);
    }
  };


  const handleInventoryFeatureToggle = async (featureId) => {
    const next = toggleFeature(formData.features, featureId);
    setFormData((prev) => ({ ...prev, features: next }));
    if (!editingClient) return;
    const { error } = await supabase.from('clients').update({ features: next }).eq('id', editingClient.id);
    if (error) {
      console.error('Error updating features:', error);
      return;
    }
    setEditingClient((prev) => (prev ? { ...prev, features: next } : prev));
  };

  const uploadSystemPhoto = async (clientId, file, systemType) => {
    const safeName = String(file.name || `${systemType}.jpg`).replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `${clientId}/${Date.now()}_${safeName}`;
    const contentType = file.type || 'image/jpeg';
    const { error: uploadError } = await supabase.storage.from('client-documents').upload(path, file, {
      contentType,
      upsert: false,
    });
    if (uploadError) throw uploadError;
    const { data: inserted, error: insertError } = await supabase.from('client_documents').insert({
      client_id: clientId,
      agent_id: editingClient.agent_id || profile.id,
      title: `${systemType} photo`,
      category: 'Other',
      file_name: file.name,
      file_path: path,
      file_type: contentType,
      mime_type: contentType,
      file_size: file.size,
      document_type: 'Other',
    }).select('id').maybeSingle();
    if (insertError) throw insertError;
    return { path, name: file.name, documentId: inserted && inserted.id };
  };

  const saveInventoryItem = async (form) => {
    if (!editingClient || !form.systemType) return;
    setSavingInventory(true);
    try {
      let photo = null;
      if (form.photoFile) {
        try {
          photo = await uploadSystemPhoto(editingClient.id, form.photoFile, form.systemType);
        } catch (error) {
          console.error('Error uploading item photo:', error);
          showToast('The item will save. The photo did not upload.');
        }
      }
      const rows = clientSystems[editingClient.id] || [];
      const merged = mergeClientSystems(editingClient, rows).find((system) => system.id === form.systemType);
      const existingRow = rows.find((row) => row.system_type === form.systemType);
      const existingData = existingRow && existingRow.system_data && Object.keys(existingRow.system_data).length
        ? existingRow.system_data
        : ((merged && merged.data) || {});
      const systemDataPayload = buildSystemData(existingData, { ...form, photo }, new Date().getFullYear());
      const { error } = await supabase.from('client_systems').upsert({
        client_id: editingClient.id,
        agent_id: editingClient.agent_id || profile.id,
        system_type: form.systemType,
        system_data: systemDataPayload,
      }, { onConflict: 'client_id,system_type' });
      if (error) throw error;

      const nextFeatures = featuresAfterInventorySave({
        features: formData.features,
        systems: systemsMapFromRows(rows, form.systemType, systemDataPayload),
      });
      if (nextFeatures.join('|') !== (formData.features || []).join('|')) {
        await supabase.from('clients').update({ features: nextFeatures }).eq('id', editingClient.id);
        setFormData((prev) => ({ ...prev, features: nextFeatures }));
        setEditingClient((prev) => (prev ? { ...prev, features: nextFeatures } : prev));
        try {
          await reassignTasks(editingClient.id, profile.id, formData.property_type, nextFeatures);
        } catch (taskError) {
          console.warn('Could not reassign tasks:', taskError);
        }
      }

      setClientSystems((prev) => {
        const list = (prev[editingClient.id] || []).filter((row) => row.system_type !== form.systemType);
        list.push({
          client_id: editingClient.id,
          system_type: form.systemType,
          system_data: systemDataPayload,
        });
        return { ...prev, [editingClient.id]: list };
      });
      setInventoryEditor(null);
    } catch (error) {
      console.error('Error saving inventory item:', error);
      showToast('Could not save that item. Please try again.');
      throw error;
    } finally {
      setSavingInventory(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const problem = propertyPhotoError(file);
    if (problem) {
      showToast(problem);
      return;
    }
    if (!editingClient || !editingClient.id) {
      setInvitePhoto(file);
      setInvitePhotoName(file.name || 'Home photo');
      showToast('Photo ready. It saves with the homeowner.');
      return;
    }

    setUploading(true);
    try {
      const publicUrl = await uploadAgentPropertyPhoto(editingClient.id, file);
      setFormData((prev) => ({ ...prev, property_photo_url: publicUrl }));
      showToast('Photo uploaded. Click Save Changes to keep it.');
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast(error.message || 'Error uploading photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const saveInvitePhoto = async (clientId) => {
    if (!invitePhoto || !clientId) return;
    try {
      const publicUrl = await uploadAgentPropertyPhoto(clientId, invitePhoto);
      const { error } = await supabase.from('clients').update({ property_photo_url: publicUrl }).eq('id', clientId);
      if (error) throw error;
      setInvitePhoto(null);
      setInvitePhotoName('');
    } catch (error) {
      console.error('Error uploading invite photo:', error);
      showToast('Homeowner added. The photo did not upload. You can add it from Edit Property.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (intakeBlocked) {
      showToast('Your subscription is not active, so new homeowners cannot be added.');
      return;
    }
    if (formData.county_pending) {
      showToast('Pick the county for this ZIP.');
      return;
    }
    if (!validState(formData.state)) {
      setStateError('Enter a 2-letter state.');
      return;
    }
    setStateError('');
    const profileFields = parsedHomeProfile(formData);
    if (!profileFields.ok) {
      showToast(Object.values(profileFields.errors).join('\n'));
      return;
    }
    const homePatch = profileFields.patch;

    setSavingHomeowner(true);
    try {
      const { ok, body } = await agentFetch('/api/homeowners', {
        method: 'POST',
        body: JSON.stringify({
          send_invite: sendInvite,
          homeowner: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            email: formData.email,
            phone: formData.phone,
            address: formData.address,
            city: formData.city,
            state: stateCode(formData.state),
            zip_code: formData.zip_code,
            property_county_fips: formData.property_county_fips || null,
            property_county_source: formData.property_county_source || null,
            property_type: canonicalPropertyType(formData.property_type) || 'single_family',
            features: factColumns(formData, homePatch).features,
            year_built: formData.year_built,
            square_feet: formData.square_feet,
            notes: formData.notes
          },
          property_details: Object.assign({
            property_photo_url: formData.property_photo_url,
            is_team_client: formData.is_team_client || false,
            hvac_install_years: formData.hvac_install_years,
            hvac_types: formData.hvac_types,
            pool_pump_install_year: formData.pool_pump_install_year,
            pool_heater_install_year: formData.pool_heater_install_year,
          }, factColumns(formData, homePatch))
        })
      });

      if (body.outcome === 'linked') {
        await saveInvitePhoto(body.id);
        setInviteCode(body.invite_code || '');
        const inviteOrigin = siteBaseFromLocation(window.location);
        setInviteLink(body.invite_code
          ? `${inviteOrigin}/login?homeowner_code=${body.invite_code}`
          : inviteOrigin);
      setInviteResult({ ...body, email: body.email || formData.email });
      setShowModal(false);
      setShowInviteModal(true);
      setFormData(emptyHomeownerForm(profile?.id));
      await loadClients();
      if (onStatsUpdate) onStatsUpdate();
      return;
    }

    if (body.duplicate || body.outcome === 'existing' || body.outcome === 'skipped_duplicate') {
        setDuplicateClient({
          id: body.id,
          name: body.name || `${formData.first_name} ${formData.last_name}`.trim()
        });
        setShowModal(false);
        return;
      }

      if (!ok) {
        if (body.code === 'limit_reached' || body.outcome === 'limit_reached') {
          const clientLimit = poolUsage ? poolUsage.limit : homeownerLimit(profile);
          const shared = Boolean(poolUsage && poolUsage.shared);
          const text = limitReachedMessage(clientLimit, shared, poolUsage && poolUsage.addon);
          showToast(isNativeApp() ? `${text} ${NATIVE_PLAN_NOTICE}` : text);
        } else if (body.code === 'subscription_inactive' || body.outcome === 'subscription_inactive') {
          showToast('Your subscription is not active, so new homeowners cannot be added.');
        } else if (body.code === 'validation_failed' && body.errors) {
          showToast(Object.values(body.errors).join('\n'));
        } else if (body.code === 'intake_not_ready') {
          showToast('Homeowner intake is not available until the database update is applied.');
        } else {
          showToast(body.error || 'Could not add this homeowner. Please try again.');
        }
        return;
      }

      await saveInvitePhoto(body.id);
      setInviteCode(body.invite_code || '');
      const inviteOrigin = siteBaseFromLocation(window.location);
      setInviteLink(body.invite_code
        ? `${inviteOrigin}/login?homeowner_code=${body.invite_code}`
        : inviteOrigin);
      setInviteResult({ ...body, email: body.email || formData.email });
      setShowModal(false);
      setShowInviteModal(true);
      setFormData(emptyHomeownerForm(profile?.id));
      await loadClients();
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      console.error('Error adding client:', error);
      showToast(error.message || 'Could not add this homeowner. Please try again.');
    } finally {
      setSavingHomeowner(false);
    }
  };

  const openInviteComposer = async ({ email, code }) => {
    const message = inviteMessage({
      agentName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
      inviteCode: code,
      siteBase: siteBaseFromLocation(window.location),
    });
    if (isNativeApp()) {
      try {
        const { Share } = await import('@capacitor/share');
        await Share.share({
          title: 'Your Pacarion invite',
          text: message,
          dialogTitle: 'Email invite',
        });
        return true;
      } catch (error) {
        const text = String((error && error.message) || error || '');
        if (/cancel/i.test(text)) return false;
      }
    }
    return openExternalUrl(inviteMailto({
      email,
      subject: 'Your Pacarion invite',
      body: message,
    }));
  };

  const deliverClientInvite = async (client) => {
    if (!client || !client.id || inviteSendingRef.current) return;
    inviteSendingRef.current = true;
    setInviteSending(true);
    let requestFailed = false;
    let body = null;
    try {
      const response = await agentFetch('/api/homeowners/invite', {
        method: 'POST',
        body: JSON.stringify({ client_ids: [client.id] }),
      });
      body = response.body;
      requestFailed = !response.ok;
    } catch (error) {
      requestFailed = true;
    }
    let next = resolveInviteSend(body, requestFailed);
    if (next.state === 'composer') {
      const opened = await openInviteComposer({
        email: client.email,
        code: client.invite_code || inviteCode,
      });
      if (!opened) {
        next = {
          state: 'blocked',
          toast: 'Could not open Mail.',
          line: 'Not emailed yet.',
          invite_status: 'not_sent',
          invite_error: null,
          invite_channel: null,
        };
      }
    }
    try {
      if (next.toast) {
        showToast(next.toast, next.state === 'blocked' ? 'error' : 'success');
      }
      setInviteResult((current) => {
        if (!current || current.id !== client.id) return current;
        return {
          ...current,
          invite_status: next.invite_status,
          invite_error: next.invite_error,
          invite_channel: next.invite_channel,
        };
      });
    if (next.state === 'emailed') {
      savedClientPatches.current.set(client.id, { invite_status: 'sent' });
      setClients((rows) => projectRows(rows, savedClientPatches.current, removedClientIds.current));
      try {
        await loadClients();
      } catch (error) {
        // The toast already confirmed the send.
      }
    }
    } finally {
      inviteSendingRef.current = false;
      setInviteSending(false);
    }
  };

  const handleEdit = (client) => {
    setConfirmDelete(false);
    setEditingClient(client);
    const profile = readHomeProfile(client);
    setFormData({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      address: client.address || '',
      city: client.city || '',
      state: client.state || '',
      zip_code: client.zip_code || '',
      property_county_fips: client.property_county_fips || '',
      property_county_source: client.property_county_source || '',
      notes: client.notes || '',
      property_type: canonicalPropertyType(client.property_type) || 'single_family',
      construction_type: profile.construction_type,
      exterior_finish: profile.exterior_finish,
      foundation_type: profile.foundation_type,
      roof_material: profile.roof_material,
      roof_year: profile.roof_year,
      warnings: profile.warnings,
      features: normalizeFeatureList(client.features || []).features,
      property_photo_url: client.property_photo_url || '',
      // System tracking fields
      hvac_count: profile.hvac_count,
      hvac_install_years: client.hvac_install_years || [null],
      hvac_types: client.hvac_types || ['Central AC'],
      roof_type: client.roof_type || '',
      roof_install_year: client.roof_install_year || null,
      water_heater_type: client.water_heater_type || '',
      water_heater_install_year: client.water_heater_install_year || null,
      water_source: client.water_source || [],
      irrigation_source: client.irrigation_source || [],
      well_pump_install_year: client.well_pump_install_year || null,
      sewer_type: client.sewer_type || [],
      septic_install_year: client.septic_install_year || null,
      pool_pump_install_year: client.pool_pump_install_year || null,
      pool_heater_install_year: client.pool_heater_install_year || null,
      year_built: client.year_built || null,
      square_feet: client.square_feet || null,
      filter_type: client.filter_type || '',
      hvac_filter_sizes: client.hvac_filter_sizes || [],
    });
    setInvitePhoto(null);
    setInvitePhotoName('');
    setShowModal(true);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    const typed = (name, fallback) => fieldValue(form, name, fallback);
    const nextName = {
      first_name: String(typed('first_name', formData.first_name) || '').trim(),
      last_name: String(typed('last_name', formData.last_name) || '').trim(),
      email: String(typed('email', formData.email) || '').trim(),
      phone: typed('phone', formData.phone),
      address: typed('address', formData.address),
      city: typed('city', formData.city),
      state: stateCode(typed('state', formData.state)),
      zip_code: typed('zip_code', formData.zip_code),
    };
    if (formData.county_pending) {
      showToast('Pick the county for this ZIP.');
      return;
    }
    if (!validState(nextName.state)) {
      setStateError('Enter a 2-letter state.');
      return;
    }
    setStateError('');
    const profileFields = parsedHomeProfile(formData);
    if (!profileFields.ok) {
      showToast(Object.values(profileFields.errors).join('\n'));
      return;
    }
    const homePatch = profileFields.patch;
    const facts = factColumns(formData, homePatch);
    const propertyType = canonicalPropertyType(formData.property_type) || 'single_family';

    try {
      const { error } = await supabase
        .from('clients')
        .update(Object.assign({
          first_name: nextName.first_name,
          last_name: nextName.last_name,
          email: nextName.email,
          phone: nextName.phone,
          address: nextName.address,
          city: nextName.city,
          state: nextName.state,
          zip_code: nextName.zip_code,
          property_county_fips: formData.property_county_fips || null,
          property_county_source: formData.property_county_source || null,
          notes: formData.notes,
          property_type: propertyType,
          property_photo_url: formData.property_photo_url,
          hvac_install_years: formData.hvac_install_years,
          hvac_types: formData.hvac_types,
          pool_pump_install_year: formData.pool_pump_install_year,
          pool_heater_install_year: formData.pool_heater_install_year,
        }, facts))
        .eq('id', editingClient.id);

      if (error) throw error;

      const beforeFacts = homeFactsSignature(editingClient) + '~' + homeProfileSignature(editingClient);
      const afterFacts = homeFactsSignature(Object.assign({}, formData, facts)) + '~' + homeProfileSignature(formData);

      if (beforeFacts !== afterFacts) {
        try {
          const tasksAssigned = await reassignTasks(
            editingClient.id,
            profile.id,
            propertyType,
            facts.features
          );
          devLog(`Tasks reassigned due to property changes: ${tasksAssigned} tasks`);
        } catch (taskError) {
          console.warn('Could not reassign tasks:', taskError);
        }
      }

      savedClientPatches.current.set(editingClient.id, nextName);
      setClients((rows) => projectRows(rows, savedClientPatches.current, removedClientIds.current));
      setShowModal(false);
      setEditingClient(null);
      setFormData(emptyHomeownerForm(profile?.id));

      await loadClients();
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      console.error('Error updating client:', error);
      showToast('Error updating client. Please try again.');
    }
  };

  const confirmArchive = async () => {
    if (!archiveClient) return;
    const restoring = archiveClient.status === 'archived';
    const status = restoring ? restoredHomeownerStatus(archiveClient) : 'archived';
    setArchiving(true);
    try {
      const { error } = await supabase
        .from('clients')
        .update({ status })
        .eq('id', archiveClient.id);
      if (error) throw error;
      showToast(restoring
        ? `${archiveClient.first_name} counts toward your limit again.`
        : `${archiveClient.first_name} is archived and no longer counts toward your limit. Their account stays.`);
      savedClientPatches.current.set(archiveClient.id, { status });
      setClients((rows) => projectRows(rows, savedClientPatches.current, removedClientIds.current));
      setArchiveClient(null);
      await loadClients();
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      console.error('Error archiving client:', error);
      const message = String((error && error.message) || '');
      showToast(message.indexOf('current home') !== -1
        ? 'This home stays archived because they already have a current home.'
        : 'Could not update that homeowner. Please try again.');
    } finally {
      setArchiving(false);
    }
  };

  const archiveAction = (client, stopEvent, quiet) => {
    if (!client || client.agent_id !== profile.id) return null;
    const archived = client.status === 'archived';
    if (!archived && !isActiveHomeowner(client)) return null;
    const label = archived ? 'Restore' : 'Archive';
    const quietArchive = quiet && !archived;
    return (
      <button
        type="button"
        aria-label={`${label} ${client.first_name} ${client.last_name}`}
        style={{
          ...styles.actionBtn,
          marginRight: '0.5rem',
          background: quietArchive ? 'transparent' : (archived ? '#334155' : '#92400e'),
          color: quietArchive ? colors.textSecondary : 'white',
          border: quietArchive ? `1px solid ${colors.border}` : 'none',
          fontWeight: quietArchive ? 500 : styles.actionBtn.fontWeight,
        }}
        onClick={(event) => {
          if (stopEvent) event.stopPropagation();
          setCardMenuId(null);
          setArchiveClient(client);
        }}
      >
        {label}
      </button>
    );
  };

  const handleDelete = async (id) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      removedClientIds.current.add(id);
      savedClientPatches.current.delete(id);
      setClients((rows) => projectRows(rows, savedClientPatches.current, removedClientIds.current));
      await loadClients();
      setShowModal(false);
      setEditingClient(null);
      setConfirmDelete(false);
      setDetailClient(null);
      if (onStatsUpdate) onStatsUpdate();
    } catch (error) {
      console.error('Error deleting client:', error);
      showToast('Error deleting client. Please try again.');
    }
  };


  const ownedCount = clients.filter((client) => (
    client.agent_id === profile.id && isActiveHomeowner(client)
  )).length;
  const clientLimit = poolUsage ? poolUsage.limit : homeownerLimit(profile);
  const usageCount = poolUsage ? poolUsage.used : ownedCount;
  const usageShared = Boolean(poolUsage && poolUsage.shared);
  const usageReady = profile?.team_id ? poolChecked : clientsReady;
  const usageLevel = usageReady ? homeownerUsageLevel(usageCount, clientLimit) : 'ok';
  const addsPaused = usageLevel === 'paused';
  const intakeBlocked = profile?.role !== 'super_admin'
    && !['trialing', 'active'].includes(profile?.subscription_status || '');
  const pendingCount = clients.filter((client) => (
    client.agent_id === profile.id
    && client.status === 'invited'
    && client.invite_status === 'not_sent'
  )).length;

  const detailRow = detailClient
    ? (clients.find((row) => row.id === detailClient.id) || detailClient)
    : null;
  const detailInvite = showInviteSendAction(detailRow);
  const detailInviteLabel = !detailRow
    ? ''
    : (detailInvite === 'fresh'
      ? 'New code'
      : (detailRow.invite_status === 'sent' || detailRow.invite_status === 'failed' ? 'Resend' : 'Invite'));

  const filteredClients = clients.filter(client => {
    if (batchFilter && client.import_batch_id !== batchFilter) return false;
    const haystack = `${client.first_name} ${client.last_name} ${client.email}`.toLowerCase();
    return haystack.includes(searchTerm.toLowerCase());
  }).slice().sort((left, right) => {
    if (!missedSort) return 0;
    const diff = ((missedByClient[right.id] && missedByClient[right.id].count) || 0)
      - ((missedByClient[left.id] && missedByClient[left.id].count) || 0);
    if (diff) return diff;
    return `${left.last_name} ${left.first_name}`.localeCompare(`${right.last_name} ${right.first_name}`);
  });

  return (
    <div style={{ minWidth: 0, maxWidth: '100%' }}>
      <HomeownerGraceBanner
        profile={profile}
        used={(profile?.team_id ? poolChecked : clientsReady)
          ? usageCount
          : null}
        colors={colors}
        onBuyPlus={() => {
          if (typeof window !== 'undefined') window.location.hash = '#billing-plus';
          if (onNavigate) onNavigate('settings');
        }}
        onBuyMax={() => {
          if (typeof window !== 'undefined') window.location.hash = '#billing-max';
          if (onNavigate) onNavigate('settings');
        }}
        onArchive={() => {
          const node = document.getElementById('homeowner-list');
          if (node) node.scrollIntoView({ block: 'start' });
        }}
      />
      <div style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.55rem', flexWrap: 'wrap', minWidth: 0 }}>
        <h2 data-tour="clients" style={{...styles.pageTitle, color: colors.text, margin: 0}}>Client Management</h2>
        {clientsReady ? (
          <span
            role="status"
            aria-label={activeClientCount(clients, profile.id) + ' active clients'}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.2rem 0.65rem',
              borderRadius: '999px',
              background: isDark ? 'rgba(89, 196, 196, 0.18)' : 'rgba(89, 196, 196, 0.14)',
              color: isDark ? '#59c4c4' : '#0f766e',
              fontSize: '0.78rem',
              fontWeight: 700,
            }}
          >
            {activeClientCount(clients, profile.id) + ' active'}
          </span>
        ) : null}
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap'}}>
          <span style={{fontSize: '0.9rem', color: colors.textSecondary}}>
            {(profile?.team_id ? poolChecked : clientsReady)
              ? homeownerUsageLabel(usageCount, clientLimit, usageShared)
              : '…'}
          </span>
          <button
            type="button"
            style={{...styles.btnSecondary, ...secondaryButtonStyle(isDark), padding: '0.65rem 1rem', opacity: intakeBlocked ? 0.6 : 1}}
            onClick={() => {
              if (intakeBlocked) {
                showToast('Your subscription is not active, so new homeowners cannot be added.');
                return;
              }
              if (addsPaused) {
                const text = limitReachedMessage(clientLimit, usageShared, poolUsage && poolUsage.addon);
                showToast(isNativeApp() ? `${text} ${NATIVE_PLAN_NOTICE}` : text);
                return;
              }
              setShowImport(true);
            }}
          >
            Import CSV
          </button>
          {pendingCount > 0 && (
            <button
              type="button"
              style={{...styles.btnSecondary, padding: '0.65rem 1rem'}}
              onClick={async () => {
                const { ok, body } = await agentFetch('/api/homeowners/invite', {
                  method: 'POST',
                  body: JSON.stringify({ pending: true })
                });
                if (!ok) {
                  showToast(body.error || inviteErrorMessage(body.code) || 'Could not send invites.');
                  return;
                }
                const results = body.results || [];
                const sent = results.filter((row) => row.invite_status === 'sent').length;
                const waiting = results.filter((row) => row.error === 'invite_cap').length;
                showToast(waiting
                  ? `Sent ${sent}. ${waiting} more are still waiting for tomorrow's invite limit.`
                  : `Sent ${sent} invite${sent === 1 ? '' : 's'}.`);
                await loadClients();
              }}
            >
              Send pending invites ({pendingCount})
            </button>
          )}
          {isWebApp() && onOpenIntegrations && (
            <button
              type="button"
              style={{...styles.btnSecondary, ...secondaryButtonStyle(isDark), padding: '0.65rem 1rem'}}
              onClick={onOpenIntegrations}
            >
              Integrations
            </button>
          )}
          <button
            style={{...styles.btnPrimary, opacity: intakeBlocked ? 0.6 : 1}}
            onClick={() => {
              if (intakeBlocked) {
                showToast('Your subscription is not active, so new homeowners cannot be added.');
                return;
              }
              if (addsPaused) {
                const text = limitReachedMessage(clientLimit, usageShared, poolUsage && poolUsage.addon);
                showToast(isNativeApp() ? `${text} ${NATIVE_PLAN_NOTICE}` : text);
                return;
              }
              setEditingClient(null);
              setShowModal(true);
            }}
          >
            + Add Homeowner
          </button>
        </div>
      </div>

      {batchFilter && (
        <p style={{margin: '0 0 1rem', color: colors.text}}>
          Showing homeowners from this import.{' '}
          <button type="button" style={{...styles.actionBtn}} onClick={() => setBatchFilter(null)}>Show everyone</button>
        </p>
      )}

      {showImport && (
        <HomeownerImport
          profile={profile}
          onClose={() => setShowImport(false)}
          onFinished={async (batchId) => {
            setBatchFilter(batchId);
            setShowImport(false);
            await loadClients();
            if (onStatsUpdate) onStatsUpdate();
          }}
        />
      )}

      {intakeBlocked && (
        <p style={{margin: '0 0 1rem', color: '#92400e'}}>
          Your subscription is {profile.subscription_status || 'inactive'}. You can keep existing homeowners, but you can't add new ones until billing is active.
        </p>
      )}

      {!intakeBlocked && usageReady && homeownerUsageWarning(usageCount, clientLimit, usageShared) ? (
        <p style={{margin: '0 0 1rem', color: '#92400e'}}>
          {homeownerUsageWarning(usageCount, clientLimit, usageShared)}
        </p>
      ) : null}

      <div id="homeowner-list" style={{...styles.card, background: colors.surface, border: `1px solid ${colors.border}`, minWidth: 0, maxWidth: '100%', overflow: 'hidden'}}>
        <input
          type="text"
          placeholder="Search clients by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{...styles.searchBox, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '-0.25rem 0 0.75rem' }}>
          <button
            type="button"
            onClick={() => setMissedSort((current) => !current)}
            style={{
              ...styles.actionBtn,
              background: missedSort ? '#991b1b' : '#334155',
            }}
          >
            {missedSort ? 'Sorted by missed' : 'Sort by missed'}
          </button>
        </div>

        {/* Desktop Table View */}
        {window.innerWidth >= 768 ? (
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={{...styles.th, width: '15%', background: colors.surfaceAlt, color: colors.text}}>Name</th>
                  <th style={{...styles.th, width: '20%', background: colors.surfaceAlt, color: colors.text}}>Email</th>
                  <th style={{...styles.th, width: '12%', background: colors.surfaceAlt, color: colors.text}}>Invite Code</th>
                  <th style={{...styles.th, width: '18%', background: colors.surfaceAlt, color: colors.text}}>Property Type</th>
                  <th style={{...styles.th, width: '10%', background: colors.surfaceAlt, color: colors.text}}>Status</th>
                  <th style={{...styles.th, width: '12%', background: colors.surfaceAlt, color: colors.text}}>Invite</th>
                  <th style={{...styles.th, width: '25%', whiteSpace: 'nowrap', background: colors.surfaceAlt, color: colors.text}}>Actions</th>
                </tr>
              </thead>
            <tbody>
              {filteredClients.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{...styles.emptyState, color: colors.textSecondary}}>
                    {clientsReady ? 'No homeowners yet. Click "Add Homeowner" to get started!' : 'Loading homeowners…'}
                  </td>
                </tr>
              ) : (
                filteredClients.map(client => {
                  const propertyTypeLabel = propertyTypes.find(pt => pt.id === client.property_type)?.label || 'Single Family Home';
                  const inviteAction = showInviteSendAction(client);

                  return (
                    <tr key={client.id}>
                      <td style={{...styles.td, color: colors.text}}>
                        <button
                          type="button"
                          onClick={() => onOpenClient && onOpenClient(client)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            color: '#59c4c4',
                            fontWeight: 600,
                            cursor: 'pointer',
                            fontSize: 'inherit',
                            textAlign: 'left'
                          }}
                        >
                          {client.first_name} {client.last_name}
                        </button>
                        {(() => {
                          const missed = missedEntry(missedByClient, client.id);
                          if (!missed) return null;
                          return (
                            <div title={missed.tip} style={{ marginTop: '0.2rem', fontSize: '0.75rem', fontWeight: 700, color: '#991b1b' }}>
                              {missed.text}
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{...styles.td, color: colors.text}}>{client.email}</td>
                      <td style={{...styles.td, color: colors.text}}>
                        <span style={{...styles.code, background: colors.surface}}>{client.invite_code}</span>
                      </td>
                      <td style={{...styles.td, color: colors.text}}>{propertyTypeLabel}</td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.badge,
                          ...(client.status === 'active' ? styles.badgeActive :
                              client.status === 'invited' ? styles.badgeInvited :
                              styles.badgeInactive)
                        }}>
                          {client.status.toUpperCase()}
                        </span>
                      </td>
                      <td style={{...styles.td, color: colors.text}}>{inviteColumnLabel(client)}</td>
                      <td style={{...styles.td, whiteSpace: 'nowrap'}}>
                        {inviteAction && (
                          <button
                            style={{...styles.actionBtn, marginRight: '0.5rem', background: '#0f766e'}}
                            onClick={() => deliverClientInvite(client)}
                          >
                            {inviteAction === 'fresh'
                              ? 'New invite code'
                              : (client.invite_status === 'sent' || client.invite_status === 'failed' ? 'Resend invite' : 'Send invite')}
                          </button>
                        )}
                        <button
                          type="button"
                          style={{...styles.actionBtn, marginRight: '0.5rem', background: '#334155'}}
                          onClick={() => setDetailClient(client)}
                        >
                          Details
                        </button>
                        <button
                          style={{...styles.actionBtn, marginRight: '0.5rem'}}
                          onClick={() => handleEdit(client)}
                        >
                          Edit
                        </button>
                        {archiveAction(client)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        ) : (
          /* Mobile Card View */
          <div style={styles.clientCards}>
            {filteredClients.length === 0 ? (
              <div style={{...styles.emptyState, color: colors.textSecondary}}>
                {clientsReady ? 'No homeowners yet. Click "Add Homeowner" to get started!' : 'Loading homeowners…'}
              </div>
            ) : (
              filteredClients.map(client => {
                const subtitle = clientCardSubtitle(client);
                const menuOpen = cardMenuId === client.id;
                const archive = archiveAction(client, true, true);

                return (
                  <div
                    key={client.id}
                    data-testid="client-mobile-card"
                    style={{...styles.clientCard, background: colors.surfaceAlt, border: `1px solid ${colors.border}`}}
                  >
                    <div style={styles.clientCardHeader}>
                      <h3 style={{...styles.clientName, color: colors.text, margin: 0}}>
                        {client.first_name} {client.last_name}
                      </h3>
                      <span style={{
                        ...styles.badge,
                        ...(client.status === 'active' ? styles.badgeActive :
                            client.status === 'invited' ? styles.badgeInvited :
                            styles.badgeInactive)
                      }}>
                        {client.status.toUpperCase()}
                      </span>
                    </div>
                    {subtitle ? (
                      <div style={{...styles.clientInfo, color: colors.textSecondary, marginBottom: '0.85rem'}}>
                        {subtitle}
                      </div>
                    ) : null}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <button
                        type="button"
                        style={{ ...styles.btnPrimary, flex: '1 1 auto', minHeight: '44px' }}
                        onClick={() => {
                          setCardMenuId(null);
                          setDetailClient(client);
                        }}
                      >
                        View client
                      </button>
                      {archive && (
                        <div style={{ position: 'relative', flex: '0 0 auto' }}>
                          <button
                            type="button"
                            aria-label={`More options for ${client.first_name} ${client.last_name}`}
                            aria-haspopup="menu"
                            aria-expanded={menuOpen}
                            onClick={() => setCardMenuId(menuOpen ? null : client.id)}
                            style={{
                              ...styles.actionBtn,
                              minWidth: '44px',
                              minHeight: '44px',
                              background: 'transparent',
                              color: colors.text,
                              border: `1px solid ${colors.border}`,
                              fontSize: '1.25rem',
                              lineHeight: 1,
                              padding: '0.4rem 0.7rem',
                            }}
                          >
                            ⋯
                          </button>
                          {menuOpen && (
                            <div
                              role="menu"
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: 'calc(100% + 4px)',
                                zIndex: 5,
                                minWidth: '10rem',
                                padding: '0.35rem',
                                borderRadius: '0.5rem',
                                background: colors.surface,
                                border: `1px solid ${colors.border}`,
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                              }}
                            >
                              {archive}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {detailRow && (
        <ClientDetailPanel
          client={detailRow}
          systems={clientSystems[detailRow.id] || []}
          onClose={() => setDetailClient(null)}
          onOpenTasks={(row) => {
            setDetailClient(null);
            if (onOpenClient) onOpenClient(row);
          }}
          onEdit={(row) => {
            setDetailClient(null);
            handleEdit(row);
          }}
          inviteLabel={detailInviteLabel}
          onInvite={detailInvite ? () => deliverClientInvite(detailRow) : null}
          onRequestReport={async (row) => {
            const result = await requestHomeHistoryReport(row.id);
            if (result && result.ok) {
              showToast('Requested');
            } else {
              showToast((result && result.error) || 'The request could not be sent.');
            }
          }}
        />
      )}

      {/* Add/Edit Client Modal */}
      {showModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={() => {
          setShowModal(false);
          setEditingClient(null);
        }}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>{editingClient ? 'Edit Homeowner' : 'Add Homeowner'}</h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={() => {
                setShowModal(false);
                setEditingClient(null);
              }}>
                ✕
              </button>
            </div>
            <form onSubmit={editingClient ? handleUpdate : handleSubmit}>
              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>First Name *</label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Last Name *</label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  required
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                />
              </div>

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Street address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  placeholder="123 Main St"
                />
              </div>

              <div style={{display: 'grid', gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 4.5rem) minmax(0, 5.5rem)', gap: '0.75rem', minWidth: 0, maxWidth: '100%'}}>
                <div style={styles.formGroup}>
                  <label style={{...styles.label, color: colors.text}}>City</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={{...styles.label, color: colors.text}}>State</label>
                  <input
                    type="text"
                    maxLength={2}
                    required
                    aria-invalid={stateError ? 'true' : 'false'}
                    name="state"
                    value={formData.state}
                    onChange={(e) => {
                      setStateError('');
                      setFormData({...formData, state: e.target.value.toUpperCase()});
                    }}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${stateError ? '#991b1b' : colors.border}`, color: colors.text}}
                    placeholder="FL"
                  />
                  {stateError ? <p role="alert" style={{ margin: '0.35rem 0 0', color: '#991b1b', fontSize: '0.9rem' }}>{stateError}</p> : null}
                </div>
                <div style={styles.formGroup}>
                  <label style={{...styles.label, color: colors.text}}>ZIP</label>
                  <input
                    type="text"
                    name="zip_code"
                    value={formData.zip_code}
                    onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                    style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                    placeholder="32801"
                  />
                </div>
              </div>
              <CountyField
                zip={formData.zip_code}
                state={formData.state}
                fips={formData.property_county_fips}
                source={formData.property_county_source}
                colors={colors}
                onChange={(county) => setFormData((current) => ({ ...current, ...county }))}
              />

              <div style={styles.formGroup}>
                <label style={{...styles.label, color: colors.text}}>Property Type</label>
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData({...formData, property_type: e.target.value})}
                  style={{...styles.input, background: colors.surfaceAlt, border: `2px solid ${colors.border}`, color: colors.text}}
                >
                  {propertyTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginTop: '0.75rem' }}>
                <h3 style={{ margin: '0 0 0.35rem', color: colors.text, fontSize: '1.05rem' }}>Home details</h3>
                <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>
                  Same questions the homeowner answers during setup.
                </p>
                <HomeProfileQuestions
                  mode="editor"
                  value={formData}
                  onChange={setFormData}
                />
              </div>

              <div style={{ marginTop: '1rem' }} data-testid="agent-home-facts">
                <HomeFactsFields value={formData} onChange={setFormData} />
              </div>

              {editingClient && (
                <div style={{marginTop: '1.25rem', minWidth: 0, maxWidth: '100%'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem'}}>
                    <h3 style={{margin: 0, color: colors.text, fontSize: '1.05rem'}}>Appliances</h3>
                    <button
                      type="button"
                      onClick={() => setInventoryEditor({ systemType: '', data: null })}
                      style={{...styles.btnPrimary, padding: '0.55rem 0.9rem'}}
                    >
                      Add item
                    </button>
                  </div>
                  {mergeClientSystems(editingClient, clientSystems[editingClient.id] || []).filter((system) => !{ hvac: true, roof: true, water_heater: true, well_pump: true, septic_system: true }[system.id]).length === 0 ? (
                    <p style={{margin: 0, color: colors.textSecondary, fontSize: '0.95rem'}}>
                      Pick a type, and add a name or year if you know it. Details can wait.
                    </p>
                  ) : (
                    <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                      {mergeClientSystems(editingClient, clientSystems[editingClient.id] || []).filter((system) => !{ hvac: true, roof: true, water_heater: true, well_pump: true, septic_system: true }[system.id]).map((system) => {
                        const brand = readBrand(system.data);
                        const year = readInstallYear(system.data);
                        return (
                          <button
                            key={system.id}
                            type="button"
                            onClick={() => setInventoryEditor({ systemType: system.id, data: system.data || {} })}
                            style={{
                              textAlign: 'left',
                              width: '100%',
                              minWidth: 0,
                              boxSizing: 'border-box',
                              padding: '0.75rem 0.9rem',
                              borderRadius: '0.75rem',
                              border: `1px solid ${colors.border}`,
                              background: colors.surfaceAlt,
                              color: colors.text,
                            }}
                          >
                            <div style={{fontWeight: 650}}>{system.name}</div>
                            {(brand || year) && (
                              <div style={{fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.2rem'}}>
                                {[brand, year && `Installed ${year}`].filter(Boolean).join(' · ')}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div style={{ marginTop: '1.5rem' }} data-testid="agent-property-photo">
                <h3 style={{ margin: '0 0 0.35rem', color: colors.text }}>Home photo</h3>
                <p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>
                  Optional. JPG, PNG, or iPhone HEIC. Same photo the homeowner and the monthly note use.
                </p>
                <input
                  type="file"
                  data-testid="agent-property-photo-input"
                  accept={PROPERTY_PHOTO_ACCEPT}
                  onChange={handleFileUpload}
                  disabled={uploading}
                  style={{
                    ...styles.input,
                    background: colors.surfaceAlt,
                    border: `2px solid ${colors.border}`,
                    color: colors.text,
                    padding: '0.5rem',
                  }}
                />
                {uploading && (
                  <p style={{ fontSize: '0.85rem', color: '#59c4c4', marginTop: '0.5rem' }}>Uploading photo...</p>
                )}
                {invitePhotoName && !editingClient && (
                  <p style={{ margin: '0.5rem 0 0', color: colors.text }}>{invitePhotoName}</p>
                )}
                <StoredPropertyPhoto
                  stored={formData.property_photo_url}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', objectFit: 'cover', marginTop: '0.75rem' }}
                />
              </div>

              {!editingClient && (
                <p style={{margin: '1rem 0 0', color: colors.textSecondary, fontSize: '0.95rem'}}>
                  Home facts can be added after this homeowner is saved.
                </p>
              )}

              {!editingClient && (
                <label style={{display: 'flex', gap: '0.5rem', alignItems: 'center', justifyContent: 'flex-start', width: 'fit-content', marginTop: '1.25rem', color: colors.text}}>
                  <input
                    type="checkbox"
                    checked={sendInvite}
                    onChange={(e) => setSendInvite(e.target.checked)}
                    style={{ width: 'auto', height: 'auto', flex: '0 0 auto', margin: 0 }}
                  />
                  Email the invite now
                </label>
              )}

              <div style={{marginTop: '1.5rem'}}>
                <button type="submit" style={styles.btnPrimary} disabled={savingHomeowner}>
                  {editingClient ? 'Save Changes' : (savingHomeowner ? 'Adding...' : 'Add Homeowner')}
                </button>
                {editingClient && (
                  <button
                    type="button"
                    style={{ ...styles.btnPrimary, background: 'transparent', color: colors.text, border: `1px solid ${colors.border}`, marginLeft: '0.5rem' }}
                    disabled={rebuildingTasks}
                    onClick={async () => {
                      setRebuildingTasks(true);
                      try {
                        await reassignTasks(
                          editingClient.id,
                          profile.id,
                          editingClient.property_type,
                          editingClient.features || []
                        );
                        showToast('Tasks rebuilt from the saved home facts. Completed tasks stayed.');
                      } catch (error) {
                        console.error('Could not rebuild tasks', error);
                        showToast('Could not rebuild tasks.');
                      } finally {
                        setRebuildingTasks(false);
                      }
                    }}
                  >
                    {rebuildingTasks ? 'Rebuilding...' : 'Rebuild tasks'}
                  </button>
                )}
                <button
                  type="button"
                  style={{...styles.btnPrimary, background: '#666', marginLeft: '0.5rem'}}
                  onClick={() => {
                    setShowModal(false);
                    setEditingClient(null);
                    setConfirmDelete(false);
                  }}
                >
                  Cancel
                </button>
              </div>
              {editingClient && (
                <div style={{ marginTop: '1.5rem', paddingTop: '1rem', borderTop: `1px solid ${colors.border}` }}>
                  {!confirmDelete ? (
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(true)}
                      style={{ background: 'transparent', border: 'none', color: '#b42318', fontWeight: 650, cursor: 'pointer', minHeight: '44px', padding: 0 }}
                    >
                      Delete homeowner
                    </button>
                  ) : (
                    <div>
                      <p style={{ color: colors.text, marginTop: 0 }}>
                        Delete {editingClient.first_name} {editingClient.last_name}? Their home record will be removed. This cannot be undone.
                      </p>
                      <button
                        type="button"
                        onClick={() => handleDelete(editingClient.id)}
                        style={{ ...styles.btnPrimary, background: '#b42318', marginRight: '0.5rem' }}
                      >
                        Delete homeowner
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmDelete(false)}
                        style={{ ...styles.btnPrimary, background: '#666' }}
                      >
                        Keep homeowner
                      </button>
                    </div>
                  )}
                </div>
              )}
            </form>
          </div>
        </ModalLayer>
      )}

      {/* Invite Link Modal */}
      {showInviteModal && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={() => setShowInviteModal(false)}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>
                {inviteResult?.outcome === 'linked'
                  ? (inviteCode ? 'This homeowner already has an account' : 'Homeowner added to your list')
                  : 'Client Invited Successfully!'}
              </h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={() => setShowInviteModal(false)}>
                ✕
              </button>
            </div>
            <div>
              <p style={{marginBottom: '0.5rem', fontSize: '1rem', color: colors.text}}>
                {inviteResult?.outcome === 'linked' && !inviteCode
                  ? 'They already use Pacarion. This home is now on your list.'
                  : inviteResult?.outcome === 'linked'
                    ? 'They already use Pacarion. Send this code so they can accept you as their sponsor.'
                    : 'Share this invite code with your homeowner:'}
              </p>
              {inviteCode ? (
              <div style={{
                background: '#59c4c4',
                color: 'white',
                padding: '1.5rem',
                borderRadius: '0.75rem',
                fontSize: '2.5rem',
                fontWeight: 'bold',
                letterSpacing: '0.3em',
                textAlign: 'center',
                marginBottom: '1rem',
                fontFamily: 'monospace'
              }}>
                {inviteCode}
              </div>
              ) : null}
              {inviteResult?.outcome !== 'linked' && (
              <p style={{margin: '0 0 0.75rem', color: colors.text, fontWeight: 600}} data-testid="invite-delivery">
                {invitePopupLine(inviteResult)}
              </p>
              )}
              {inviteResult?.outcome === 'linked' && inviteCode ? (
                <p style={{margin: '1rem 0', color: colors.textSecondary, fontSize: '0.9rem', lineHeight: 1.6}}>
                  They enter this code in Pacarion to accept you as their sponsor. It does not create a second home.
                </p>
              ) : inviteCode ? (
                <p style={{margin: '1rem 0', color: colors.textSecondary, fontSize: '0.9rem', lineHeight: 1.6}}>
                  They can open <strong>{inviteLink}</strong>, choose <strong>Homeowner Login</strong>, and enter this code.
                </p>
              ) : null}
              <div style={{display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap'}}>
                {inviteCode ? (
                <button
                  style={{...styles.btnPrimary, background: '#10b981', flex: 1}}
                  onClick={() => {
                    const message = inviteMessage({
                      agentName: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
                      inviteCode,
                      siteBase: siteBaseFromLocation(window.location),
                    });
                    navigator.clipboard.writeText(message).then(() => {
                      showToast('Invite message copied.');
                    }).catch(() => {
                      showToast(message);
                    });
                  }}
                >
                  Copy invite message
                </button>
                ) : null}
                {inviteResult?.outcome !== 'linked' && inviteResult?.invite_status !== 'sent' && inviteResult?.id && (
                  <button
                    type="button"
                    disabled={inviteSending}
                    style={{...styles.btnPrimary, flex: 1, opacity: inviteSending ? 0.6 : 1}}
                    onClick={() => deliverClientInvite(inviteResult)}
                  >
                    {inviteSending ? 'Sending...' : 'Email invite'}
                  </button>
                )}
                <button
                  style={{...styles.btnPrimary, background: '#666', flex: 1}}
                  onClick={() => setShowInviteModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </ModalLayer>
      )}

      {duplicateClient && (
        <ModalLayer className="app-modal-layer" style={styles.modal} onClick={() => setDuplicateClient(null)}>
          <div className="app-modal-card" style={{...styles.modalContent, background: colors.surface, border: `1px solid ${colors.border}`}} onClick={(e) => e.stopPropagation()}>
            <div className="app-modal-header" style={{...styles.modalHeader, background: colors.surface}}>
              <h2 style={{color: colors.text, margin: 0}}>Already in your list</h2>
              <button type="button" className="app-modal-close" aria-label="Close" style={{...styles.closeBtn, color: colors.text}} onClick={() => setDuplicateClient(null)}>✕</button>
            </div>
            <p role="alert" style={{color: colors.text}}>This homeowner is already in your list.</p>
            <div style={{display: 'flex', gap: '0.75rem', marginTop: '1rem', flexWrap: 'wrap'}}>
              <button
                style={{...styles.btnPrimary, flex: 1}}
                onClick={() => {
                  setSearchTerm(duplicateClient.name);
                  setDuplicateClient(null);
                }}
              >
                View
              </button>
              <button
                style={{...styles.btnPrimary, background: '#0f766e', flex: 1}}
                onClick={async () => {
                  const row = clients.find((client) => client.id === duplicateClient.id) || duplicateClient;
                  await deliverClientInvite(row);
                  setDuplicateClient(null);
                }}
              >
                Resend invite
              </button>
              <button style={{...styles.btnPrimary, background: '#666', flex: 1}} onClick={() => setDuplicateClient(null)}>
                Close
              </button>
            </div>
          </div>
        </ModalLayer>
      )}

      <ConfirmDialog
        open={Boolean(archiveClient)}
        title={archiveClient && archiveClient.status === 'archived' ? 'Restore homeowner' : 'Archive homeowner'}
        message={archiveClient && archiveClient.status === 'archived'
          ? `Restore ${archiveClient.first_name}? They count toward your homeowner limit again.`
          : `Archive ${archiveClient ? archiveClient.first_name : 'this homeowner'}? They keep their account and their records. They no longer count toward your homeowner limit.`}
        confirmLabel={archiveClient && archiveClient.status === 'archived' ? 'Restore' : 'Archive'}
        busy={archiving}
        onCancel={() => { if (!archiving) setArchiveClient(null); }}
        onConfirm={confirmArchive}
      />

      {inventoryEditor && editingClient && (
        <InventoryItemForm
          colors={colors}
          existingData={inventoryEditor.data}
          initialType={inventoryEditor.systemType}
          features={formData.features}
          saving={savingInventory}
          onToggleFeature={handleInventoryFeatureToggle}
          onCancel={() => { if (!savingInventory) setInventoryEditor(null); }}
          onSave={saveInventoryItem}
        />
      )}

    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem'
  },
  pageTitle: {
    fontSize: 'clamp(1.3rem, 5vw, 1.8rem)',
    margin: 0
  },
  card: {
    background: 'white',
    padding: window.innerWidth < 768 ? '1rem' : '2rem',
    borderRadius: '1rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  searchBox: {
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    width: '100%',
    maxWidth: window.innerWidth < 768 ? '100%' : '400px',
    marginBottom: '1.5rem',
    fontSize: 'clamp(0.9rem, 3vw, 1rem)',
    boxSizing: 'border-box'
  },
  clientCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem'
  },
  clientCard: {
    borderRadius: '0.5rem',
    padding: '1rem'
  },
  clientCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
    gap: '0.5rem'
  },
  clientName: {
    margin: 0,
    fontSize: '1.1rem',
    flex: 1,
    minWidth: 0,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word'
  },
  clientCardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1rem'
  },
  clientInfo: {
    fontSize: '0.9rem',
    color: '#555',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
    minWidth: 0,
    maxWidth: '100%'
  },
  clientCardFooter: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    marginBottom: '2rem',
    WebkitOverflowScrolling: 'touch'
  },
  table: {
    width: 'max-content',
    minWidth: '100%',
    borderCollapse: 'collapse'
  },
  th: {
    background: '#f5f5f5',
    padding: '1rem',
    textAlign: 'left',
    fontWeight: 600,
    borderBottom: '2px solid #ddd'
  },
  td: {
    padding: '1rem',
    borderBottom: '1px solid #eee'
  },
  code: {
    background: '#f5f5f5',
    padding: '0.25rem 0.5rem',
    borderRadius: '0.25rem',
    fontFamily: 'monospace'
  },
  badge: {
    display: 'inline-block',
    padding: '0.25rem 0.75rem',
    borderRadius: '1rem',
    fontSize: '0.85rem',
    fontWeight: 600
  },
  badgeActive: {
    background: '#d4edda',
    color: '#155724'
  },
  badgeInactive: {
    background: '#f8d7da',
    color: '#721c24'
  },
  badgeInvited: {
    background: '#fff3cd',
    color: '#856404'
  },
  emptyState: {
    textAlign: 'center',
    color: '#666',
    padding: '2rem'
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)',
    color: 'white',
    border: 'none',
    padding: window.innerWidth < 768 ? '0.65rem 1rem' : '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 'clamp(0.85rem, 3vw, 1rem)',
    whiteSpace: 'nowrap'
  },
  actionBtn: {
    background: '#59c4c4',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: 'clamp(0.85rem, 3vw, 0.9rem)'
  },
  deleteBtn: {
    background: '#59c4c4',
    color: 'white',
    border: 'none',
    padding: '0.5rem 1rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontSize: 'clamp(0.85rem, 3vw, 0.9rem)'
  },
  modal: {
    display: 'flex',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.7)',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: window.innerWidth < 768 ? '1rem' : '0'
  },
  modalContent: {
    background: 'white',
    padding: window.innerWidth < 768 ? '1.5rem' : '2rem',
    borderRadius: '1rem',
    maxWidth: '600px',
    width: window.innerWidth < 768 ? '100%' : '90%',
    maxHeight: '90vh',
    overflowY: 'auto'
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    cursor: 'pointer',
    color: '#666'
  },
  formGroup: {
    marginBottom: '1rem'
  },
  label: {
    display: 'block',
    fontWeight: 600,
    marginBottom: '0.5rem',
    color: '#333'
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    border: '2px solid #ddd',
    borderRadius: '0.5rem',
    fontSize: 'clamp(0.9rem, 3vw, 1rem)',
    boxSizing: 'border-box'
  },
  checkboxGrid: {
    display: 'grid',
    gridTemplateColumns: window.innerWidth < 768 ? '1fr' : 'repeat(2, 1fr)',
    gap: '0.75rem'
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  inviteLink: {
    background: '#f5f5f5',
    padding: '1rem',
    borderRadius: '0.5rem',
    fontFamily: 'monospace',
    wordBreak: 'break-all'
  },
  btnSecondary: {
    background: '#f3f4f6',
    color: '#333',
    border: '2px solid #d1d5db',
    padding: window.innerWidth < 768 ? '0.65rem 1rem' : '0.75rem 1.5rem',
    borderRadius: '0.5rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: 'clamp(0.85rem, 3vw, 1rem)',
    whiteSpace: 'nowrap'
  },
  infoBox: {
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem'
  },
  resultsSummary: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '1.5rem',
    borderRadius: '0.5rem',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  resultsItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '0.25rem'
  }
};

export default ClientManagement;
